import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';
import { GroupMemberList } from './GroupMemberList';
import { GroupZoneSubscriptions } from './GroupZoneSubscriptions';

export interface EventGroup {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  active?: boolean;
  createdAt: string;
  updatedAt: string;
  _count: {
    memberships: number;
    zoneSubscriptions: number;
  };
}

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

const panelStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    fontSize: '1.25rem',
    color: TEXT_DARK,
    margin: 0,
  },
  createBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  card: {
    padding: '1rem 1.25rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
  },
  cardName: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '1rem',
    color: TEXT_DARK,
    margin: 0,
  },
  cardDescription: {
    fontSize: '0.85rem',
    color: TEXT_SECONDARY,
    margin: '0.25rem 0 0',
    lineHeight: 1.4,
  },
  cardMeta: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.6rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  metaItem: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
  },
  badge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
  },
  badgeActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  badgeInactive: {
    backgroundColor: '#fce4ec',
    color: '#c62828',
  },
  actions: {
    display: 'flex',
    gap: '2px',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: '0.75rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '8px',
    border: `1px dashed ${BORDER_LIGHT}`,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  // Create group form styles
  formSection: {
    padding: '1.25rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    marginBottom: '1.25rem',
  },
  formTitle: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '1rem',
    color: TEXT_DARK,
    margin: '0 0 1rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: TEXT_DARK,
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    minHeight: '80px',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  charCount: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    textAlign: 'right' as const,
    marginTop: '0.25rem',
  },
  formActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.25rem',
  },
  saveBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  fieldError: {
    fontSize: '0.78rem',
    color: '#d32f2f',
    marginTop: '0.25rem',
  },
  toggle: {
    position: 'relative' as const,
    width: '36px',
    height: '20px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    padding: 0,
    flexShrink: 0,
  },
  toggleKnob: {
    position: 'absolute' as const,
    top: '2px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s',
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  renameOverlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  renameModal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
  renameTitle: {
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    fontSize: '1.1rem',
    color: TEXT_DARK,
    margin: '0 0 1rem',
  },
  renameInput: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  renameActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  renameCancelBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  renameSaveBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

interface EventGroupsPanelProps {
  onSelectGroup?: (group: EventGroup) => void;
}

export function EventGroupsPanel({ onSelectGroup }: EventGroupsPanelProps) {
  const [groups, setGroups] = useState<EventGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  // Create group form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formError, setFormError] = useState('');
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Rename state
  const [renaming, setRenaming] = useState<EventGroup | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  async function fetchGroups() {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient<EventGroup[]>('/event-groups');
      setGroups(data);
    } catch {
      setError('Failed to load event groups.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchGroups();
  }, []);

  function handleOpenCreateForm() {
    setShowCreateForm(true);
    setFormName('');
    setFormDescription('');
    setFormError('');
    setNameError('');
  }

  function handleCancelCreate() {
    setShowCreateForm(false);
    setFormName('');
    setFormDescription('');
    setFormError('');
    setNameError('');
  }

  async function handleCreateSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    setNameError('');

    // Client-side validation
    const trimmedName = formName.trim();
    if (!trimmedName) {
      setNameError('Group name is required.');
      return;
    }
    if (trimmedName.length > 100) {
      setNameError('Group name must be 100 characters or less.');
      return;
    }
    if (formDescription.length > 500) {
      setFormError('Description must be 500 characters or less.');
      return;
    }

    try {
      setSubmitting(true);
      await apiClient('/event-groups', {
        method: 'POST',
        body: {
          name: trimmedName,
          description: formDescription.trim() || undefined,
        },
      });
      setShowCreateForm(false);
      setFormName('');
      setFormDescription('');
      fetchGroups();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setNameError(apiErr.message || 'An event group with this name already exists.');
      } else {
        setFormError(apiErr.message || 'Failed to create event group.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // --- Task 6.6: Active/Inactive toggle, Rename, Delete ---

  useEffect(() => {
    if (renaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renaming]);

  async function handleToggleActive(group: EventGroup) {
    try {
      setError('');
      await apiClient(`/event-groups/${group.id}`, {
        method: 'PATCH',
        body: { isActive: !group.isActive },
      });
      await fetchGroups();
    } catch {
      setError(`Failed to update status for "${group.name}".`);
    }
  }

  function handleStartRename(group: EventGroup) {
    setRenaming(group);
    setRenameValue(group.name);
  }

  async function handleSaveRename() {
    if (!renaming || !renameValue.trim()) return;
    if (renameValue.trim() === renaming.name) {
      setRenaming(null);
      return;
    }
    try {
      setRenameSaving(true);
      setError('');
      await apiClient(`/event-groups/${renaming.id}`, {
        method: 'PATCH',
        body: { name: renameValue.trim() },
      });
      setRenaming(null);
      await fetchGroups();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError(apiErr.message || 'An event group with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to rename group.');
      }
    } finally {
      setRenameSaving(false);
    }
  }

  async function handleDelete(group: EventGroup) {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${group.name}"? This will remove all memberships and zone subscriptions. This action cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      setError('');
      await apiClient(`/event-groups/${group.id}`, { method: 'DELETE' });
      await fetchGroups();
    } catch {
      setError(`Failed to delete "${group.name}".`);
    }
  }

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.header}>
        <h2 style={panelStyles.heading}>Event Groups</h2>
        {!showCreateForm && (
          <button
            style={panelStyles.createBtn}
            onClick={handleOpenCreateForm}
            aria-label="Create a new event group"
          >
            + Create Group
          </button>
        )}
      </div>

      {error && <div style={panelStyles.error}>{error}</div>}

      {/* Create Group Inline Form */}
      {showCreateForm && (
        <div style={panelStyles.formSection}>
          <h3 style={panelStyles.formTitle}>Create New Group</h3>
          {formError && <div style={panelStyles.error}>{formError}</div>}
          <form onSubmit={handleCreateSubmit}>
            <div style={panelStyles.field}>
              <label style={panelStyles.label} htmlFor="group-name">
                Name <span style={{ color: '#d32f2f' }}>*</span>
              </label>
              <input
                id="group-name"
                style={nameError ? panelStyles.inputError : panelStyles.input}
                type="text"
                value={formName}
                onChange={(e) => {
                  setFormName(e.target.value);
                  if (nameError) setNameError('');
                }}
                maxLength={100}
                placeholder="Enter group name"
                autoFocus
                disabled={submitting}
              />
              {nameError && <div style={panelStyles.fieldError}>{nameError}</div>}
              <div style={panelStyles.charCount}>{formName.length}/100</div>
            </div>
            <div style={panelStyles.field}>
              <label style={panelStyles.label} htmlFor="group-description">
                Description
              </label>
              <textarea
                id="group-description"
                style={panelStyles.textarea}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                maxLength={500}
                placeholder="Optional description for this group"
                disabled={submitting}
              />
              <div style={panelStyles.charCount}>{formDescription.length}/500</div>
            </div>
            <div style={panelStyles.formActions}>
              <button
                type="submit"
                style={{
                  ...panelStyles.saveBtn,
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
                disabled={submitting}
              >
                {submitting ? 'Saving…' : 'Save Group'}
              </button>
              <button
                type="button"
                style={panelStyles.cancelBtn}
                onClick={handleCancelCreate}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={panelStyles.loading}>Loading groups…</div>
      ) : groups.length === 0 && !showCreateForm ? (
        <div style={panelStyles.empty}>
          <p style={{ margin: 0 }}>No event groups yet.</p>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem' }}>
            Create a group to target announcements and manage zone subscriptions.
          </p>
        </div>
      ) : (
        <div style={panelStyles.list}>
          {groups.map((group) => (
            <div
              key={group.id}
              style={{
                ...panelStyles.card,
                ...(expandedGroupId === group.id ? { borderColor: PRIMARY } : {}),
              }}
              onClick={() => {
                setExpandedGroupId(expandedGroupId === group.id ? null : group.id);
                onSelectGroup?.(group);
              }}
              role="button"
              tabIndex={0}
              aria-label={`View details for ${group.name}`}
              aria-expanded={expandedGroupId === group.id}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpandedGroupId(expandedGroupId === group.id ? null : group.id);
                  onSelectGroup?.(group);
                }
              }}
            >
              <div style={panelStyles.cardTop}>
                <div style={panelStyles.cardInfo}>
                  <h3 style={panelStyles.cardName}>{group.name}</h3>
                  {group.description && (
                    <p style={panelStyles.cardDescription}>{group.description}</p>
                  )}
                  <div style={panelStyles.cardMeta}>
                    <span style={panelStyles.metaItem}>
                      👥 {group._count.memberships} member{group._count.memberships !== 1 ? 's' : ''}
                    </span>
                    <span style={panelStyles.metaItem}>
                      📡 {group._count.zoneSubscriptions} zone{group._count.zoneSubscriptions !== 1 ? 's' : ''}
                    </span>
                    <span
                      style={{
                        ...panelStyles.badge,
                        ...(group.isActive ? panelStyles.badgeActive : panelStyles.badgeInactive),
                      }}
                    >
                      {group.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div style={panelStyles.actions}>
                  <button
                    style={{
                      ...panelStyles.toggle,
                      backgroundColor: group.isActive ? '#2e7d32' : '#bdbdbd',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(group);
                    }}
                    title={group.isActive ? 'Deactivate group' : 'Activate group'}
                    aria-label={group.isActive ? `Deactivate ${group.name}` : `Activate ${group.name}`}
                    aria-pressed={group.isActive}
                  >
                    <span
                      style={{
                        ...panelStyles.toggleKnob,
                        left: group.isActive ? '18px' : '2px',
                      }}
                    />
                  </button>
                  <button
                    style={iconBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartRename(group);
                    }}
                    title="Rename"
                    aria-label={`Rename ${group.name}`}
                  >
                    ✏️
                  </button>
                  <button
                    style={iconBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(group);
                    }}
                    title="Delete"
                    aria-label={`Delete ${group.name}`}
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Expanded Group Detail */}
              {expandedGroupId === group.id && (
                <div
                  style={{ marginTop: '1rem', borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '0.5rem' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GroupMemberList groupId={group.id} groupName={group.name} />
                  <GroupZoneSubscriptions groupId={group.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rename Modal */}
      {renaming && (
        <div
          style={panelStyles.renameOverlay}
          onClick={() => setRenaming(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rename-dialog-title"
        >
          <div style={panelStyles.renameModal} onClick={(e) => e.stopPropagation()}>
            <h3 id="rename-dialog-title" style={panelStyles.renameTitle}>
              Rename Group
            </h3>
            <input
              ref={renameInputRef}
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') setRenaming(null);
              }}
              maxLength={100}
              style={panelStyles.renameInput}
              aria-label="New group name"
            />
            <div style={panelStyles.renameActions}>
              <button
                style={panelStyles.renameCancelBtn}
                onClick={() => setRenaming(null)}
                disabled={renameSaving}
              >
                Cancel
              </button>
              <button
                style={panelStyles.renameSaveBtn}
                onClick={handleSaveRename}
                disabled={renameSaving || !renameValue.trim()}
              >
                {renameSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
